/**
 * Le chrono ne compte jamais de ticks : il stocke des horodatages et dérive l'écoulé
 * à l'affichage.
 *
 * iOS ne ralentit pas les minuteurs quand l'écran s'éteint, il SUSPEND le contexte
 * JavaScript. Un chrono qui incrémente un compteur toutes les secondes afficherait
 * douze minutes à la quarante-troisième, parce qu'il aurait passé le reste du temps
 * gelé. En partant de `Date.now()`, être gelé quarante minutes est sans effet : la
 * première image après le réveil est juste.
 *
 * La persistance passe par localStorage, seul cas du projet où il bat IndexedDB : sur
 * `pagehide` iOS n'accorde que quelques millisecondes, et une transaction asynchrone
 * n'a aucune garantie de se terminer là où `setItem` est déjà fini quand la ligne
 * retourne.
 */
const CLE = 'aja.chrono';

const vide = (matchId = null) => ({ matchId, etat: 'arrete', demarreA: null, cumul: 0, periode: 1 });

export function lireChrono() {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return vide();
    const c = JSON.parse(brut);
    return c && typeof c === 'object' ? { ...vide(), ...c } : vide();
  } catch {
    return vide();
  }
}

function ecrireChrono(c) {
  try { localStorage.setItem(CLE, JSON.stringify(c)); } catch { /* quota ou mode privé : le chrono reste en mémoire */ }
  return c;
}

export const ecoule = (c, maintenant = Date.now()) =>
  c.cumul + (c.etat === 'marche' && c.demarreA ? maintenant - c.demarreA : 0);

export const minutes = (c, maintenant = Date.now()) => Math.floor(ecoule(c, maintenant) / 60000);

export function demarrer(c, matchId) {
  if (c.etat === 'marche') return c;
  return ecrireChrono({ ...c, matchId: matchId ?? c.matchId, etat: 'marche', demarreA: Date.now() });
}

export function pauser(c) {
  if (c.etat !== 'marche') return c;
  return ecrireChrono({ ...c, etat: 'pause', cumul: ecoule(c), demarreA: null });
}

/** La seconde période repart à 45', comme sur l'écran du stade. */
export function secondePeriode(c) {
  return ecrireChrono({ ...c, periode: 2, etat: 'marche', cumul: Math.max(ecoule(c), 45 * 60000), demarreA: Date.now() });
}

export const reinitialiser = (matchId) => ecrireChrono(vide(matchId));
export const enMarche = () => lireChrono().etat === 'marche';

/** Sauver l'état à chaque mise en arrière-plan : iOS peut tuer l'app sans prévenir. */
export function brancherChrono(lireEtat) {
  const sauver = () => { const c = lireEtat(); if (c) ecrireChrono(c); };
  window.addEventListener('pagehide', sauver);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') sauver(); });
}

export const formater = (c, maintenant = Date.now()) => {
  const total = Math.floor(ecoule(c, maintenant) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};
